// src/components/Registration/BusinessRegistration.jsx
import React, { useEffect, useLayoutEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import ProgressIndicator from './ProgressIndicator';

import Step1Identity from './steps/Step1Identity';
import Step2Contacts, {
  emptyEinForm as makeEmptyEinForm,
  emptyBrand as makeEmptyBrand,
} from './steps/Step2Contacts';
import Step3Location from './steps/Step3Location';
import Step4Agreement from './steps/Step4Agreement';
import Step5Review from './steps/Step5Review';

import brandRegistrationAPI from '../../services/brandRegistrationApi';
import { apiFetch, setOrgContext, getAuthToken, setAuthToken } from '../../api/client';

import tealBack from '../../Images/teal_dback.png';
import rypilLo from '../../Images/rypilLo.png';

const PEACH = '#EBA477';
const TOTAL_STEPS = 5;
const LOCAL_DRAFT_PREFIX = 'registrationDraft:';

/* -------------------- API base helper (always include /api) -------------------- */
/* -------------------- tiny utils -------------------- */
function splitName(fullName = '') {
  const parts = (fullName || '').trim().split(/\s+/);
  const firstName = parts.shift() || '';
  const lastName = parts.join(' ');
  return { firstName, lastName };
}
function getToken() {
  return getAuthToken();
}
function purgeAuth() {
  setAuthToken(null);
  localStorage.removeItem('user');
  localStorage.removeItem('registrationSessionId');
}

function getLocalDraftKey(sessionId) {
  return `${LOCAL_DRAFT_PREFIX}${sessionId || 'no-session'}`;
}

function readLocalDraft(sessionId) {
  try {
    const raw = localStorage.getItem(getLocalDraftKey(sessionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function writeLocalDraft(sessionId, currentStep, data) {
  try {
    const draftData = sanitizeDraftDataForLocalStorage(data);
    localStorage.setItem(
      getLocalDraftKey(sessionId),
      JSON.stringify({
        sessionId: sessionId || null,
        currentStep,
        data: draftData,
        updatedAt: new Date().toISOString(),
      })
    );
  } catch (err) {
    console.warn('Failed to persist local draft:', err);
  }
}

function clearLocalDraft(sessionId) {
  try {
    localStorage.removeItem(getLocalDraftKey(sessionId));
  } catch {}
}

function isInlineDataUrl(value) {
  return typeof value === 'string' && /^data:/i.test(value);
}

function mergeBrandWithServerAssets(serverBrand = {}, localBrand = {}) {
  const merged = { ...serverBrand, ...localBrand };
  if (!merged.logoUrl) {
    merged.logoUrl = serverBrand.logoUrl || serverBrand.logo || serverBrand.logoPreviewUrl || '';
  }
  if (!merged.logoPreviewUrl) {
    merged.logoPreviewUrl = serverBrand.logoPreviewUrl || '';
  }
  return merged;
}

function mergeStep2Data(serverStep2 = {}, localStep2 = {}) {
  const merged = { ...serverStep2, ...localStep2 };

  if (Array.isArray(serverStep2?.einForms) || Array.isArray(localStep2?.einForms)) {
    const serverForms = Array.isArray(serverStep2?.einForms) ? serverStep2.einForms : [];
    const localForms = Array.isArray(localStep2?.einForms) ? localStep2.einForms : [];
    const sourceForms = localForms.length ? localForms : serverForms;

    merged.einForms = sourceForms.map((localForm, formIdx) => {
      const serverForm = serverForms[formIdx] || {};
      const nextForm = { ...serverForm, ...localForm };
      const serverBrands = Array.isArray(serverForm?.brands) ? serverForm.brands : [];
      const localBrands = Array.isArray(localForm?.brands) ? localForm.brands : [];
      const sourceBrands = localBrands.length ? localBrands : serverBrands;
      nextForm.brands = sourceBrands.map((localBrand, brandIdx) =>
        mergeBrandWithServerAssets(serverBrands[brandIdx] || {}, localBrand || {})
      );
      return nextForm;
    });
  }

  if (Array.isArray(serverStep2?.businesses) || Array.isArray(localStep2?.businesses)) {
    const serverBusinesses = Array.isArray(serverStep2?.businesses) ? serverStep2.businesses : [];
    const localBusinesses = Array.isArray(localStep2?.businesses) ? localStep2.businesses : [];
    const sourceBusinesses = localBusinesses.length ? localBusinesses : serverBusinesses;
    merged.businesses = sourceBusinesses.map((localBiz, bizIdx) =>
      mergeBrandWithServerAssets(serverBusinesses[bizIdx] || {}, localBiz || {})
    );
  }

  return merged;
}

function mergeStep4Data(serverStep4 = {}, localStep4 = {}) {
  const merged = { ...serverStep4, ...localStep4 };
  if (serverStep4?.signature || localStep4?.signature) {
    const serverSignature = serverStep4?.signature || {};
    const localSignature = localStep4?.signature || {};
    merged.signature = { ...serverSignature, ...localSignature };
    if (!merged.signature.dataUrl) merged.signature.dataUrl = serverSignature.dataUrl || '';
  }
  if (!merged.signatureDataUrl) merged.signatureDataUrl = serverStep4?.signatureDataUrl || '';
  if (serverStep4?.legal || localStep4?.legal) {
    const serverLegal = serverStep4?.legal || {};
    const localLegal = localStep4?.legal || {};
    merged.legal = { ...serverLegal, ...localLegal };
    if (!merged.legal.signatureDataUrl) merged.legal.signatureDataUrl = serverLegal.signatureDataUrl || '';
  }
  return merged;
}

function stripStep2DraftAssets(step2 = {}) {
  if (!step2 || typeof step2 !== 'object') return step2;
  const next = { ...step2 };

  if (Array.isArray(step2.einForms)) {
    next.einForms = step2.einForms.map((form) => ({
      ...form,
      logoFile: null,
      logoPreviewUrl: '',
      logoUrl: isInlineDataUrl(form?.logoUrl) ? '' : (form?.logoUrl || ''),
      brands: Array.isArray(form?.brands)
        ? form.brands.map((brand) => ({
            ...brand,
            logoFile: null,
            logoPreviewUrl: '',
            logoUrl: isInlineDataUrl(brand?.logoUrl) ? '' : (brand?.logoUrl || ''),
          }))
        : form?.brands,
    }));
  }

  if (Array.isArray(step2.businesses)) {
    next.businesses = step2.businesses.map((business) => ({
      ...business,
      logoFile: null,
      logoPreviewUrl: '',
      logoUrl: isInlineDataUrl(business?.logoUrl) ? '' : (business?.logoUrl || ''),
    }));
  }

  return next;
}

function stripStep4DraftAssets(step4 = {}) {
  if (!step4 || typeof step4 !== 'object') return step4;
  const next = { ...step4 };
  if (isInlineDataUrl(next.signatureDataUrl)) next.signatureDataUrl = '';
  if (next.signature && typeof next.signature === 'object') {
    next.signature = { ...next.signature };
    if (isInlineDataUrl(next.signature.dataUrl)) next.signature.dataUrl = '';
  }
  if (next.legal && typeof next.legal === 'object') {
    next.legal = { ...next.legal };
    if (isInlineDataUrl(next.legal.signatureDataUrl)) next.legal.signatureDataUrl = '';
  }
  return next;
}

function sanitizeDraftDataForLocalStorage(data = {}) {
  if (!data || typeof data !== 'object') return data;
  return {
    ...data,
    step2: stripStep2DraftAssets(data.step2),
    step4: stripStep4DraftAssets(data.step4),
  };
}

function hasStepContent(value) {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return Boolean(value);
}

function sanitizeStepDataForStorage(stepNumber, stepData) {
  if (!stepData || typeof stepData !== 'object') return stepData;
  if (Number(stepNumber) !== 1) return stepData;
  const { password: _omitPassword, ...rest } = stepData;
  return rest;
}

function sanitizeStepDataForApi(stepNumber, stepData) {
  if (!stepData || typeof stepData !== 'object') return stepData;
  if (Number(stepNumber) === 2) return stripStep2DraftAssets(stepData);
  if (Number(stepNumber) === 4) return stripStep4DraftAssets(stepData);
  if (Number(stepNumber) === 3 && stepData.step2Patch) {
    return {
      ...stepData,
      step2Patch: stripStep2DraftAssets(stepData.step2Patch),
    };
  }
  return stepData;
}

function mergeRegistrationData(serverData, localData) {
  if (!localData || typeof localData !== 'object') return serverData;
  const merged = { ...serverData };
  Object.keys(merged).forEach((key) => {
    if (hasStepContent(localData[key])) {
      if (key === 'step2') {
        merged[key] = mergeStep2Data(serverData[key], localData[key]);
      } else if (key === 'step4') {
        merged[key] = mergeStep4Data(serverData[key], localData[key]);
      } else {
        merged[key] = localData[key];
      }
    }
  });
  return merged;
}

function allBrandsOnlineOnlyFromStep2(step2 = {}) {
  const einForms = Array.isArray(step2?.einForms) ? step2.einForms : [];
  if (!einForms.length) return false;
  return einForms.every((form) =>
    Array.isArray(form?.brands) &&
    form.brands.length > 0 &&
    form.brands.every((brand) => !!brand?.isOnlineOnly)
  );
}

/* ---------- coerce server payloads into step shapes ---------- */
function ensureMarketObject(m) {
  if (typeof m === 'object' && m !== null && m.marketKey) return m;
  const name = String(m).trim();
  if (name) return { marketKey: name, name: name };
  return null;
}

function computeBusinessKey(business, idx) {
  if (business?.tempBusinessKey) return String(business.tempBusinessKey);
  const cleanEin = String(business?.ein || business?.einTaxId || '').trim();
  return cleanEin ? `EIN:${cleanEin}` : `IDX:${idx}`;
}
function toStep2(existing) {
  const dedupeStrings = (arr = [], limit = 10) =>
    Array.from(
      new Set(
        (Array.isArray(arr) ? arr : [])
          .map((s) => String(s || '').trim())
          .filter(Boolean)
      )
    ).slice(0, limit);

  const normalizeOwnershipType = (value, footprint) => {
    const raw = String(value || '').trim().toLowerCase();
    if (raw === 'franchise' || raw === 'franchisee') return 'Franchisee';
    if (raw === 'corporate') return 'Corporate';
    const fp = String(footprint || '').trim().toLowerCase();
    if (fp === 'franchise' || fp === 'franchisee') return 'Franchisee';
    if (fp === 'corporate' || fp === 'chain' || fp === 'national' || fp === 'regional') return 'Corporate';
    return 'Independent';
  };

  const normalizeBrand = (brand = {}, fallbackName = '') => {
    const base = makeEmptyBrand();
    const markets = (Array.isArray(brand.markets) ? brand.markets : [])
      .map(ensureMarketObject)
      .filter(Boolean);
    const regionalFromBrand = Array.isArray(brand?.marketsInput?.regionalList)
      ? brand.marketsInput.regionalList.map((s) => String(s || '').trim())
      : markets.map((m) => m.marketKey || m.name).filter(Boolean);
    const regionalList = dedupeStrings(regionalFromBrand, 10);

    return {
      ...base,
      ...brand,
      brandName: (brand.brandName || brand.dbaName || fallbackName || '').trim(),
      businessCategory: brand.businessCategory || brand.category || '',
      baselineContribution: String(brand.baselineContribution ?? brand.baseContributionPct ?? 2),
      tagline: brand.tagline || '',
      description: (brand.description || '').slice(0, 128),
      logoFile: null,
      logoPreviewUrl: '',
      logoUrl: brand.logoUrl || brand.logo || brand.logoPreviewUrl || '',
      isOnlineOnly: !!brand.isOnlineOnly,
      primaryWebsiteUrl: brand.primaryWebsiteUrl || '',
      locationSearch: {
        mode: brand?.locationSearch?.mode || (regionalList.length ? 'area' : ''),
        values: Array.isArray(brand?.locationSearch?.values)
          ? brand.locationSearch.values
          : regionalList,
        searchName: brand?.locationSearch?.searchName || (brand.brandName || brand.dbaName || fallbackName || '').trim(),
        pendingInput: '',
      },
      markets,
      marketKey: brand.marketKey || markets[0]?.marketKey || '',
      marketsInput: { regionalList },
    };
  };

  const normalizeEinForm = (form = {}, idx = 0) => {
    const base = makeEmptyEinForm();
    const ownershipType = normalizeOwnershipType(form.ownershipType, form.footprint);
    const aliases = dedupeStrings(form.aliases || form.dbaAliases || [], 8);

    const brandsSource =
      Array.isArray(form.brands) && form.brands.length
        ? form.brands
        : (() => {
            const primary = {
              brandName: form.brandName || form.dbaName || '',
              businessCategory: form.businessCategory || form.category || '',
              baselineContribution: form.baselineContribution ?? form.baseContributionPct ?? 2,
              tagline: form.tagline || '',
              description: form.description || '',
              logoUrl: form.logoUrl || form.logoPreviewUrl || '',
              logoPreviewUrl: '',
              isOnlineOnly: !!form.isOnlineOnly,
              primaryWebsiteUrl: form.primaryWebsiteUrl || '',
              markets: form.markets,
              marketsInput: form.marketsInput,
            };
            const extras = Array.isArray(form.extraBrandNames)
              ? form.extraBrandNames.map((name) => ({ brandName: name }))
              : [];
            return [primary, ...extras];
          })();

    const normalizedBrands = brandsSource
      .map((brand, brandIdx) => normalizeBrand(brand, brandIdx === 0 ? form.brandName || form.dbaName || '' : ''))
      .filter((brand, brandIdx) => brandIdx === 0 || !!brand.brandName);

    if (!normalizedBrands.length) normalizedBrands.push(normalizeBrand({}, ''));

    return {
      ...base,
      legalBusinessName: (form.legalBusinessName || form.legalEntityName || '').trim(),
      einTaxId: (form.einTaxId || form.ein || '').trim(),
      ownershipType,
      aliases,
      primaryContact: form.primaryContact || base.primaryContact,
      brands: normalizedBrands,
    };
  };

  if (existing?.step2?.einForms) {
    const einForms = existing.step2.einForms.map((f, idx) => normalizeEinForm(f, idx));
    return {
      ...existing.step2,
      einForms,
      ackAllStores: !!existing.step2.ackAllStores,
      needsExceptions: !!existing.step2.needsExceptions,
    };
  }

  const c = existing?.contacts || {};
  const existingMarkets = Array.isArray(existing?.markets)
    ? existing.markets.map(ensureMarketObject).filter(Boolean)
    : [];
  const regionalList = existingMarkets.map((m) => m?.marketKey || m?.name).filter(Boolean);

  const fallbackForm = normalizeEinForm({
    legalBusinessName: c.legalBusinessName || c.legalEntityName || '',
    einTaxId: c.einTaxId || c.ein || '',
    ownershipType: c.ownershipType || c.footprint,
    aliases: c.aliases,
    primaryContact: c.primaryContact || { name: '', email: '', phone: '' },
    brands: [
      {
        brandName: c.actualBusinessName || c.dbaName || '',
        businessCategory: c.businessCategory || c.category || '',
        baselineContribution: c.baselineContribution || c.baseContributionPct || '2',
        tagline: c.tagline || '',
        description: c.description || '',
        logoUrl: c.logoUrl || '',
        isOnlineOnly: c.isOnlineOnly || c.footprint === 'onlineonly',
        primaryWebsiteUrl: c.primaryWebsiteUrl || '',
        markets: existingMarkets,
        marketsInput: { regionalList },
      },
    ],
  });

  return { einForms: [fallbackForm], ackAllStores: false, needsExceptions: false };
}
function toStep3(existing) {
  if (
    existing?.step3?.storeLocations ||
    existing?.step3?.storeLocationsByEin ||
    existing?.step3?.storeLocationsByBusinessKey
  ) {
    return existing.step3;
  }

  const fromLocations = existing?.locations?.storeIntake;
  const fromBiz0 = Array.isArray(existing?.businesses) ? existing.businesses[0]?.locations : null;
  const storeLocations = Array.isArray(fromLocations)
    ? fromLocations
    : Array.isArray(fromBiz0)
    ? fromBiz0
    : [];

  const storeLocationsByBusinessKey = {};
  const marketsByBusinessKey = {};
  if (Array.isArray(existing?.businesses)) {
    existing.businesses.forEach((b, idx) => {
      const key = computeBusinessKey(b, idx);
      storeLocationsByBusinessKey[key] = Array.isArray(b?.locations) ? b.locations : [];
      marketsByBusinessKey[key] = (Array.isArray(b?.markets) ? b.markets : [])
        .map(ensureMarketObject)
        .filter(Boolean);
    });
  }

  const marketKey =
    Array.isArray(existing?.markets) && existing.markets.length
      ? existing.markets[0]?.marketKey || existing.markets[0]
      : (Array.isArray(existing?.businesses) && existing.businesses[0]?.markets?.[0]?.marketKey) || '';

  return {
    storeLocations,
    storeLocationsByEin: storeLocationsByBusinessKey,
    storeLocationsByBusinessKey,
    marketsByEin: marketsByBusinessKey,
    marketsByBusinessKey,
    marketKey,
  };
}

/* -------------------- direct API calls for step 1 bootstrapping -------------------- */
async function apiRegisterPending({ firstName, lastName, email, phone, password, role }) {
  return apiFetch('/auth/register-pending', {
    method: 'POST',
    body: JSON.stringify({ firstName, lastName, email, phone, password, role }),
  });
}
async function apiAuthMe() {
  try {
    return await apiFetch('/auth/me');
  } catch (err) {
    if (err?.status === 401) return null;
    throw err;
  }
}
async function apiRegStart({ email, ownerId, accountId }) {
  return apiFetch('/brand/registration/start', {
    method: 'POST',
    body: JSON.stringify({ email, ownerId, accountId }),
    orgContext: accountId ? { orgId: String(accountId), orgType: 'account' } : undefined,
  });
}
async function apiPatchStep1(sessionId, { fullName, email, phone, role, ownerId, accountId }) {
  return apiFetch(`/brand/registration/${sessionId}/step/1`, {
    method: 'PATCH',
    body: JSON.stringify({ fullName, email, phone, role, ownerId, accountId }),
    orgContext: accountId ? { orgId: String(accountId), orgType: 'account' } : undefined,
  });
}

async function apiCreateAccountForRegistration(body = {}) {
  const res = await apiFetch('/business-registration', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return res;
}

export default function BusinessRegistration() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [activeAccountId, setActiveAccountId] = useState(() => {
    if (params?.accountId) return params.accountId;
    if (typeof window !== 'undefined') {
      return localStorage.getItem('activeAccountId');
    }
    return null;
  });

  const commitAccountContext = useCallback(
    (nextId) => {
      if (!nextId) return;
      const normalized = String(nextId);
      setActiveAccountId((prev) => (prev === normalized ? prev : normalized));
      setOrgContext({ orgId: normalized, orgType: 'account' });
    },
    []
  );

  /* -------- Detect add-new flow for existing users --------
     Rules:
     - If query ?mode=add → add-new
     - If query ?mode=new → force fresh new-user flow
     - Otherwise, if path contains /add-business AND we have a token → add-new
  --------------------------------------------------------- */
  const isAddNewFlow = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const qMode = (params.get('mode') || '').toLowerCase();
    if (qMode === 'add') return true;
    if (qMode === 'new') return false;
    return !!getToken() && location.pathname.includes('/add-business');
  }, [location.pathname, location.search]);

  const [currentStep, setCurrentStep] = useState(1);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isInitializing, setIsInitializing] = useState(true);

  // per-step local cache
  const [registrationData, setRegistrationData] = useState({
    step1: {},
    step2: {},
    step3: {},
    step4: {},
    step5: {},
  });

  // Shared next-step binder provided by each step (or validator for Step 5)
  const [nextBind, setNextBind] = useState({ onClick: null, disabled: true });
  const currentStepRef = useRef(currentStep);

  // visual mapping (skip agreements in add-new → visually 4 steps)
  const SKIP_AGREEMENTS = isAddNewFlow;
  const visualTotalSteps = SKIP_AGREEMENTS ? 4 : 5;
  const visualStep = useMemo(() => {
    if (!SKIP_AGREEMENTS) return currentStep;
    return currentStep >= 5 ? 4 : currentStep;
  }, [currentStep, SKIP_AGREEMENTS]);

  // Reset the next-button binding before child passive effects run on step changes.
  useLayoutEffect(() => {
    currentStepRef.current = currentStep;
    setNextBind({ onClick: null, disabled: true });
  }, [currentStep]);

  const bindNextForStep = useCallback((stepNumber, payload) => {
    if (currentStepRef.current !== stepNumber) return;
    setNextBind(payload || { onClick: null, disabled: true });
  }, []);

  const stepBindNext = useMemo(
    () => (payload) => bindNextForStep(currentStep, payload),
    [bindNextForStep, currentStep]
  );

  useEffect(() => {
    if (params?.accountId) {
      commitAccountContext(params.accountId);
    }
  }, [params?.accountId, commitAccountContext]);

  // Initialize / restore session
  useEffect(() => {
    const initializeSession = async () => {
      try {
        setIsInitializing(true);

        const savedSessionId = localStorage.getItem('registrationSessionId');
        const applyHydratedRegistration = (sid, existing, localDraft = null) => {
          const serverData = {
            step1: existing.ownerSnapshot || existing.identity || existing.step1 || {},
            step2: toStep2(existing),
            step3: toStep3(existing),
            step4: existing.legal || existing.step4 || {},
            step5: existing.review || existing.step5 || {},
          };
          const mergedData = mergeRegistrationData(serverData, localDraft?.data);
          const localStep = Number(localDraft?.currentStep || 0);

          const completedSteps = Array.isArray(existing.completedSteps) ? existing.completedSteps : [];
          const highestCompletedStep = completedSteps.reduce((max, step) => {
            const n = Number(step);
            return Number.isFinite(n) ? Math.max(max, n) : max;
          }, 0);
          let resolvedStep = Math.min(existing.currentStep || 1, TOTAL_STEPS);
          if (highestCompletedStep > 0) {
            resolvedStep = Math.max(resolvedStep, Math.min(highestCompletedStep + 1, TOTAL_STEPS));
          }
          if (localStep > 0) resolvedStep = Math.max(resolvedStep, Math.min(localStep, TOTAL_STEPS));
          if (allBrandsOnlineOnlyFromStep2(mergedData.step2) && resolvedStep === 3) {
            resolvedStep = SKIP_AGREEMENTS ? 5 : 4;
          }
          if (SKIP_AGREEMENTS && resolvedStep === 4) resolvedStep = 5;

          setSessionId(sid);
          setRegistrationData((r) => ({ ...r, ...mergedData }));
          setCurrentStep(isAddNewFlow ? Math.max(resolvedStep, 2) : resolvedStep);
          writeLocalDraft(sid, isAddNewFlow ? Math.max(resolvedStep, 2) : resolvedStep, mergedData);
        };

        // 1) Try restore an existing sessionId from localStorage
        if (savedSessionId) {
          try {
            const existing = await brandRegistrationAPI.getRegistration(savedSessionId);
            if (existing?.accountId) {
              commitAccountContext(existing.accountId);
            }
            applyHydratedRegistration(savedSessionId, existing, readLocalDraft(savedSessionId));

            setIsInitializing(false);
            return;
          } catch {
            localStorage.removeItem('registrationSessionId');
          }
        }

        // 2) Determine auth state
        const token = getToken();
        const me = token ? await apiAuthMe() : null;

        // If token is present but invalid => purge and render Step 1
        if (token && !me) {
          purgeAuth();
          setSessionId(null);
          setCurrentStep(1);
          setIsInitializing(false);
          return;
        }

        const ownerId = me?.id;
        const fallbackEmail =
          me?.email || JSON.parse(localStorage.getItem('user') || '{}')?.email || '';

        if (me?.accountId) {
          commitAccountContext(me.accountId);
        }

        // 3) If server has an in-progress registration for this user, restore it
        if (me?.registration?.sessionId) {
          const sid = me.registration.sessionId;
          const existing = await brandRegistrationAPI.getRegistration(sid);
          localStorage.setItem('registrationSessionId', sid);
          if (existing?.accountId) {
            commitAccountContext(existing.accountId);
          }
          applyHydratedRegistration(sid, existing, readLocalDraft(sid));

          setIsInitializing(false);
          return;
        }

        // 4) If authenticated (me), start a fresh server session
        if (me) {
          const fresh = await apiRegStart({ email: fallbackEmail, ownerId, accountId: activeAccountId });
          setSessionId(fresh.sessionId);
          localStorage.setItem('registrationSessionId', fresh.sessionId);
          if (fresh?.accountId) {
            commitAccountContext(fresh.accountId);
          }

          // In add-new, prefill step1 and advance to step 2
          if (isAddNewFlow) {
            const name = [me.firstName, me.lastName].filter(Boolean).join(' ') || me.email;
            const role = me.role || 'admin';

            await apiPatchStep1(fresh.sessionId, {
              fullName: name,
              email: me.email,
              phone: me.phone,
              role,
              ownerId: me.id,
              accountId: fresh?.accountId || activeAccountId,
            });

            setRegistrationData((r) => ({
              ...r,
              step1: { fullName: name, email: me.email, phone: me.phone, role },
            }));
            setCurrentStep(2);
            writeLocalDraft(fresh.sessionId, 2, {
              step1: { fullName: name, email: me.email, phone: me.phone, role },
              step2: {},
              step3: {},
              step4: {},
              step5: {},
            });
          } else {
            setCurrentStep(1);
            writeLocalDraft(fresh.sessionId, 1, {
              step1: {},
              step2: {},
              step3: {},
              step4: {},
              step5: {},
            });
          }

          setIsInitializing(false);
          return;
        }

        // 5) Not authenticated: no session yet — Step 1 will create user + session
        {
          const localDraft = readLocalDraft(null);
          if (localDraft?.data) {
            setRegistrationData((r) => ({ ...r, ...mergeRegistrationData(r, localDraft.data) }));
          }
        }
        setSessionId(null);
        setCurrentStep(1);
      } catch (err) {
        console.error('Failed to initialize registration session:', err);
        setError('Failed to initialize registration. Please refresh and try again.');
        setCurrentStep(1);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeSession();
  }, [isAddNewFlow, SKIP_AGREEMENTS, commitAccountContext, activeAccountId]);

  // Unified step handler
  const handleNext = async (stepData, nextStepOverride) => {
    setLoading(true);
    setError('');

    try {
      // In add-new, when UI is at 1 we actually write Step 2 on the server.
      const effectiveStep = isAddNewFlow && currentStep === 1 ? 2 : currentStep;
      const sanitizedStepData = sanitizeStepDataForStorage(effectiveStep, stepData);

      // For steps > 1, we need a sessionId in hand
      if (effectiveStep > 1 && !sessionId) {
        setError('No active session. Please refresh and try again.');
        return;
      }

      if (effectiveStep === 1) {
        const hasExistingSession = !!sessionId && !!getToken();
        let sid = sessionId;
        let ownerId = null;
        let resolvedAccountId = activeAccountId || params?.accountId || null;

        if (hasExistingSession) {
          const me = await apiAuthMe();
          ownerId = me?.id || null;
          resolvedAccountId = resolvedAccountId || me?.accountId || null;
        } else {
          const { firstName, lastName } = splitName(stepData.fullName);
          const regPending = await apiRegisterPending({
            firstName,
            lastName,
            email: stepData.email,
            phone: stepData.phone,
            password: stepData.password,
            role: stepData.role || 'admin',
          });

          if (regPending.token) setAuthToken(regPending.token);
          if (regPending.user) localStorage.setItem('user', JSON.stringify(regPending.user));

          ownerId = regPending.user?.id || null;
          resolvedAccountId =
            regPending?.registration?.accountId ||
            regPending?.user?.accountId ||
            resolvedAccountId;

          if (!resolvedAccountId) {
            const fallbackAccountName =
              stepData.accountName ||
              stepData.businessName ||
              stepData.fullName ||
              stepData.email;
            const created = await apiCreateAccountForRegistration({
              accountName: fallbackAccountName,
              legalBusinessName: fallbackAccountName,
              billingEmail: stepData.email,
            });
            resolvedAccountId = created?.accountId || null;
          }

          if (regPending.registration?.sessionId) {
            sid = regPending.registration.sessionId;
            setSessionId(sid);
            localStorage.setItem('registrationSessionId', sid);
          } else if (!sid) {
            const started = await apiRegStart({
              email: stepData.email,
              ownerId,
              accountId: resolvedAccountId,
            });
            sid = started.sessionId;
            setSessionId(sid);
            localStorage.setItem('registrationSessionId', sid);
            if (started?.accountId) {
              resolvedAccountId = started.accountId;
            }
          }
        }

        if (resolvedAccountId) {
          commitAccountContext(resolvedAccountId);
        }

        await apiPatchStep1(sid, {
          fullName: stepData.fullName,
          email: stepData.email,
          phone: stepData.phone,
          role: stepData.role,
          ownerId,
          accountId: resolvedAccountId,
        });

        const nextRegistrationData = { ...registrationData, step1: sanitizedStepData };
        setRegistrationData(nextRegistrationData);
        clearLocalDraft(null);
        writeLocalDraft(sid, 2, nextRegistrationData);
        setCurrentStep(2);
        if (!params?.accountId && resolvedAccountId) {
          navigate(`/accounts/${encodeURIComponent(String(resolvedAccountId))}/registration`, {
            replace: true,
          });
        }
        return;
      }

      // Steps 2–5 (and add-new pseudo “step 1” -> server step 2)
      await brandRegistrationAPI.updateStep(
        sessionId,
        effectiveStep,
        sanitizeStepDataForApi(effectiveStep, stepData)
      );
      const nextRegistrationData = {
        ...registrationData,
        [`step${effectiveStep}`]: sanitizedStepData,
      };
      if (effectiveStep === 3 && stepData?.step2Patch) {
        nextRegistrationData.step2 = {
          ...nextRegistrationData.step2,
          ...stepData.step2Patch,
        };
      }
      setRegistrationData(nextRegistrationData);

      // Next-step routing
      let nextStep = null;
      if (nextStepOverride) {
        nextStep = SKIP_AGREEMENTS && nextStepOverride === 4 ? 5 : nextStepOverride;
      } else if (effectiveStep === 2) {
        const forms = stepData?.einForms || [];
        const needsPhysicalStep = forms.some((form) =>
          Array.isArray(form?.brands)
            ? form.brands.some((brand) => !brand?.isOnlineOnly)
            : !form?.isOnlineOnly
        );
        if (needsPhysicalStep) {
          nextStep = 3;
        } else {
          nextStep = SKIP_AGREEMENTS ? 5 : 4;
        }
      } else if (effectiveStep === 3) {
        nextStep = SKIP_AGREEMENTS ? 5 : 4;
      } else if (effectiveStep < TOTAL_STEPS) {
        nextStep = effectiveStep + 1;
      }
      writeLocalDraft(sessionId, nextStep || effectiveStep, nextRegistrationData);
      if (nextStep) setCurrentStep(nextStep);
    } catch (err) {
      console.error('Failed to save step data:', err);
      const stepKey = `step${currentStep}`;
      const nextRegistrationData = {
        ...registrationData,
        [stepKey]: sanitizeStepDataForStorage(currentStep, stepData),
      };
      if (currentStep === 3 && stepData?.step2Patch) {
        nextRegistrationData.step2 = {
          ...nextRegistrationData.step2,
          ...stepData.step2Patch,
        };
      }
      setRegistrationData(nextRegistrationData);
      writeLocalDraft(sessionId, currentStep, nextRegistrationData);

      const msg = String(err?.message || '').toLowerCase();
      const infra =
        msg.includes('server error') ||
        msg.includes('network') ||
        msg.includes('timeout') ||
        msg.includes('http 5');
      if (infra) {
        setError('We could not reach the server. Your progress is saved on this device, but not yet synced. Please retry on this step.');
      } else {
        setError(err.message || 'Failed to save your progress. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = (stepData) => {
    if (currentStep <= 1) return;
    const nextStep = currentStep - 1;
    if (stepData && typeof stepData === 'object') {
      const stepKey = `step${currentStep}`;
      const nextRegistrationData = {
        ...registrationData,
        [stepKey]: sanitizeStepDataForStorage(currentStep, stepData),
      };
      if (currentStep === 3 && stepData?.step2Patch) {
        nextRegistrationData.step2 = {
          ...nextRegistrationData.step2,
          ...stepData.step2Patch,
        };
      }
      setRegistrationData(nextRegistrationData);
      writeLocalDraft(sessionId, nextStep, nextRegistrationData);
    } else {
      writeLocalDraft(sessionId, nextStep, registrationData);
    }
    setCurrentStep(nextStep);
  };

  const completeRegistration = async () => {
    if (!sessionId) {
      setError('No active session. Please refresh and try again.');
      return;
    }
    try {
      setLoading(true);
      setError('');

      if (typeof nextBind.onClick === 'function') {
        try {
          await nextBind.onClick();
        } catch (e) {
          setError(e?.message || 'Failed to validate final review.');
          return;
        }
      }

      const result = await brandRegistrationAPI.completeRegistration(sessionId);
      const brandId =
        result?.brandId ||
        (Array.isArray(result?.brandIds) ? result.brandIds[0] : null) ||
        result?.data?.brandId ||
        null;

      localStorage.removeItem('registrationSessionId');
      clearLocalDraft(sessionId);
      if (brandId) localStorage.setItem('lastBrandId', String(brandId));

      const dashboardBase = activeAccountId
        ? `/accounts/${encodeURIComponent(String(activeAccountId))}/dashboard`
        : '/home';

      if (brandId) {
        const nextUrl = `${dashboardBase}?brandId=${encodeURIComponent(String(brandId))}`;
        navigate(nextUrl, { replace: true });
      } else {
        navigate(dashboardBase, { replace: true });
      }
    } catch (err) {
      console.error('Registration completion failed:', err);
      setError(err?.message || 'Failed to complete registration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderCurrentStep = () => {
    const stepToDisplay = currentStep;
    const stepProps = {
      data: registrationData[`step${stepToDisplay}`],
      onNext: handleNext,
      onPrevious: handlePrevious,
      loading,
      isFirstStep: !isAddNewFlow && stepToDisplay === 1,
      isLastStep: stepToDisplay === 5, // real final step remains 5
      sessionId,
      accountId: activeAccountId,
      onBindNext: stepBindNext,
    };

    if (stepToDisplay === 2) {
      stepProps.step1Data = registrationData.step1;
    }
    if (stepToDisplay === 3) {
      stepProps.step2Data = registrationData.step2;
    }
    if (stepToDisplay === 5) {
      stepProps.step1Data = registrationData.step1;
      stepProps.step2Data = registrationData.step2;
      stepProps.step3Data = registrationData.step3;
      stepProps.step4Data = registrationData.step4;
      stepProps.skipAgreements = SKIP_AGREEMENTS; // hide T&C on review if add-new
    }

    switch (stepToDisplay) {
      case 1:
        // In add-new, render Step 2 UI (but write to server step 2)
        if (isAddNewFlow) return <Step2Contacts {...stepProps} step1Data={registrationData.step1} />;
        return <Step1Identity {...stepProps} requirePassword={!(sessionId && !!getToken())} />;
      case 2:
        return <Step2Contacts {...stepProps} step1Data={registrationData.step1} />;
      case 3:
        return <Step3Location {...stepProps} step2Data={registrationData.step2} />;
      case 4:
        if (SKIP_AGREEMENTS) return <Step5Review {...stepProps} skipAgreements />;
        return <Step4Agreement {...stepProps} />;
      case 5:
        return <Step5Review {...stepProps} />;
      default:
        return <div>Invalid step: {stepToDisplay}. Session may be corrupt.</div>;
    }
  };

  const LoadingView = (
    <div style={styles.fullscreen} className="business-reg-shell">
      <div style={styles.card}>
        <div style={{ textAlign: 'center' }}>
          <div style={styles.spinner} />
          <p style={{ marginTop: 12 }}>Initializing registration...</p>
        </div>
      </div>
    </div>
  );

  const SessionErrorView = (
    <div style={styles.fullscreen} className="business-reg-shell">
      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Session Error</h3>
        <p>Failed to initialize registration session. Please refresh the page and try again.</p>
        <button onClick={() => window.location.reload()} style={styles.primaryBtn}>
          Refresh Page
        </button>
      </div>
    </div>
  );

  if (isInitializing) return LoadingView;
  // Allow Step 1 to render even when there's no session yet (unauth flow)
  if (!sessionId && currentStep !== 1) return SessionErrorView;

  // “Complete” button shows at real step 5 (visually step 4 when skipping agreements)
  const isFinal = currentStep === 5;

  return (
      <div style={styles.fullscreen} className="business-reg-shell">
        {/* Header */}
        <div style={styles.headerWrap}>
          <h1 style={styles.headerTitle}>BUSINESS REGISTRATION</h1>
          <div style={styles.headerSub}>Complete your business profile to get started</div>
        </div>

        {/* Progress — keep ProgressIndicator */}
        <div style={styles.progressWrap}>
          <div style={styles.progressCard}>
            <ProgressIndicator currentStep={visualStep} totalSteps={visualTotalSteps} />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={styles.errorCard}>
            <div style={{ flex: 1 }}>{error}</div>
            <button onClick={() => setError('')} style={styles.dismissBtn}>
              ×
            </button>
          </div>
        )}

        {/* Content + under-edge anchors */}
        <div style={styles.contentSection}>
          <div style={styles.contentCard}>{renderCurrentStep()}</div>

          {/* Badge under LEFT edge */}
          <img src={rypilLo} alt="rypil mark" style={styles.edgeBadge} />

          {/* Right control: Next (steps 1–4) OR Complete (final) */}
          {!isFinal ? (
            <button
              type="button"
              onClick={nextBind.onClick || (() => {})}
              disabled={!!nextBind.disabled || loading}
              aria-label="Next"
              title="Next"
              style={styles.nextBtn(!!nextBind.disabled || loading)}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={completeRegistration}
              disabled={loading}
              style={styles.completeBtn(loading)}
            >
              Complete Registration
            </button>
          )}
        </div>

        <style>{`.frosted-input::placeholder{color:rgba(255,255,255,0.75);}
        .business-reg-shell select {
          background: rgba(255,255,255,0.08);
          color: white;
        }
        .business-reg-shell select option,
        .business-reg-shell select optgroup {
          color: #123642;
          background: #f7fbfc;
        }
        @media (forced-colors: active) {
          .business-reg-shell select {
            forced-color-adjust: auto;
          }
        }`}</style>
      </div>
  );
}

/* ===== Styles ===== */
const styles = {
  fullscreen: {
    backgroundImage: `url(${tealBack})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    minHeight: '100vh',
    width: '100%',
    position: 'relative',
    color: 'white',
    fontFamily: 'Helvetica, Arial, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingBottom: 64,
  },
  headerWrap: { marginTop: 56, textAlign: 'center' },
  headerTitle: { margin: 0, fontSize: 36, fontWeight: 700, letterSpacing: 2 },
  headerSub: { marginTop: 8, fontSize: 18, opacity: 0.9 },
  progressWrap: { width: 'min(93vw, 1100px)', marginTop: 24 },
  progressCard: {
    width: '100%',
    background: 'rgba(255,255,255,0.10)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16,
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    padding: 16,
    boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
  },
  errorCard: {
    width: 'min(94vw, 1080px)',
    marginTop: 16,
    background: 'rgba(255, 87, 87, 0.12)',
    border: '1px solid rgba(255, 87, 87, 0.4)',
    borderRadius: 12,
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  dismissBtn: {
    background: 'transparent',
    color: 'white',
    border: 'none',
    fontSize: 22,
    cursor: 'pointer',
  },
  contentSection: {
    width: 'min(94vw, 1080px)',
    marginTop: 20,
    position: 'relative',
    marginBottom: 120,
  },
  contentCard: {
    width: '100%',
    background: 'rgba(255,255,255,0.10)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20,
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    padding: 24,
    boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
    overflow: 'hidden',
    position: 'relative',
  },
  edgeBadge: {
    position: 'absolute',
    left: 0,
    top: 'calc(100% + 14px)',
    height: 56,
    opacity: 0.95,
    pointerEvents: 'none',
    zIndex: 2,
  },
  nextBtn: (disabled) => ({
    position: 'absolute',
    right: -40,
    top: 'calc(100% + 4px)',
    minWidth: 120,
    height: 44,
    padding: '0 20px',
    background: disabled ? 'rgba(235,164,119,0.35)' : PEACH,
    border: 'none',
    borderRadius: 12,
    color: '#1d1d1d',
    fontSize: 16,
    fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
    userSelect: 'none',
    zIndex: 3,
  }),
  completeBtn: (disabled) => ({
    position: 'absolute',
    right: 0,
    top: 'calc(100% + 4px)',
    background: disabled ? 'rgba(235,164,119,0.35)' : PEACH,
    border: 'none',
    color: '#1d1d1d',
    fontWeight: 700,
    padding: '10px 16px',
    borderRadius: 12,
    cursor: disabled ? 'not-allowed' : 'pointer',
    zIndex: 3,
  }),
  card: {
    width: 'min(92vw, 560px)',
    background: 'rgba(255,255,255,0.10)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20,
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    padding: 24,
    textAlign: 'center',
    color: 'white',
    boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
  },
  spinner: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: '3px solid rgba(255,255,255,0.35)',
    borderTopColor: PEACH,
    animation: 'spin 1s linear infinite',
    margin: '0 auto',
  },
  primaryBtn: {
    background: PEACH,
    border: 'none',
    color: '#1d1d1d',
    fontWeight: 600,
    padding: '10px 16px',
    borderRadius: 12,
    cursor: 'pointer',
    marginTop: 8,
  },
};

// add spinner keyframes once
if (typeof document !== 'undefined' && !document.getElementById('br-spin-kf')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'br-spin-kf';
  styleEl.innerHTML = `@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`;
  document.head.appendChild(styleEl);
}
